import {
  actionCreator,
  persistentReducedStream,
  reducer,
} from '@ardoq/rxbeach';
import type { ArdoqId } from '@ardoq/api-types';
import type { GraphComponent } from '@ardoq/yfiles';

export type MetamodelSlideGraph = {
  graphComponent: GraphComponent;
  metamodelId: ArdoqId;
};

export type MetamodelSlideSurface = {
  graph: MetamodelSlideGraph | null;
  container: HTMLElement | null;
};

export type HtmlSlideCapture = {
  slideId: ArdoqId | null;
  isBusy: boolean;
  hasError: boolean;
  isReport: boolean;
};

export type HtmlSlideSurface = HtmlSlideCapture & {
  container: HTMLElement;
};

export type SlideCaptureSurfaceState = {
  metamodel: MetamodelSlideSurface;
  html: HtmlSlideSurface | null;
};

export const metamodelSlideGraphComponentRegistered =
  actionCreator<MetamodelSlideGraph>(
    '[slideCaptureSurface] Metamodel graph component registered'
  );
export const metamodelSlideGraphComponentUnregistered = actionCreator(
  '[slideCaptureSurface] Metamodel graph component unregistered'
);
export const metamodelSlideContainerRegistered = actionCreator<HTMLElement>(
  '[slideCaptureSurface] Metamodel container registered'
);
export const metamodelSlideContainerUnregistered = actionCreator(
  '[slideCaptureSurface] Metamodel container unregistered'
);
export const htmlSlideSurfaceRegistered = actionCreator<HtmlSlideSurface>(
  '[slideCaptureSurface] HTML surface registered'
);
export const htmlSlideSurfaceUnregistered = actionCreator(
  '[slideCaptureSurface] HTML surface unregistered'
);

const NO_SURFACES: SlideCaptureSurfaceState = {
  metamodel: { graph: null, container: null },
  html: null,
};

const updateMetamodel = (
  state: SlideCaptureSurfaceState,
  metamodel: Partial<MetamodelSlideSurface>
): SlideCaptureSurfaceState => ({
  ...state,
  metamodel: { ...state.metamodel, ...metamodel },
});

const registerGraphComponent = (
  state: SlideCaptureSurfaceState,
  graph: MetamodelSlideGraph
): SlideCaptureSurfaceState => updateMetamodel(state, { graph });

const unregisterGraphComponent = (
  state: SlideCaptureSurfaceState
): SlideCaptureSurfaceState => updateMetamodel(state, { graph: null });

const registerContainer = (
  state: SlideCaptureSurfaceState,
  container: HTMLElement
): SlideCaptureSurfaceState => updateMetamodel(state, { container });

const unregisterContainer = (
  state: SlideCaptureSurfaceState
): SlideCaptureSurfaceState => updateMetamodel(state, { container: null });

const registerHtml = (
  state: SlideCaptureSurfaceState,
  html: HtmlSlideSurface
): SlideCaptureSurfaceState => ({ ...state, html });

const unregisterHtml = (
  state: SlideCaptureSurfaceState
): SlideCaptureSurfaceState => ({ ...state, html: null });

export const slideCaptureSurface$ = persistentReducedStream(
  'slideCaptureSurface$',
  NO_SURFACES,
  [
    reducer(metamodelSlideGraphComponentRegistered, registerGraphComponent),
    reducer(metamodelSlideGraphComponentUnregistered, unregisterGraphComponent),
    reducer(metamodelSlideContainerRegistered, registerContainer),
    reducer(metamodelSlideContainerUnregistered, unregisterContainer),
    reducer(htmlSlideSurfaceRegistered, registerHtml),
    reducer(htmlSlideSurfaceUnregistered, unregisterHtml),
  ]
);
